import {
  calculateCashDepositCents,
  calculateCashDifferenceCents,
} from '@/lib/services/cash-drawers/cash-drawers.reconciliation'
import { Ledger, LedgerRow, LedgerSection } from '@/components/patterns/ledger'
import { CashDenominationCounter } from '@/components/pos/CashDenominationCounter'
import { BalancedBadge, formatTenderDifference } from '@/components/pos/CashDrawerReceipt'
import { CashDrawerExpectedDisclosure } from '@/components/pos/CashDrawerReconciliation'
import { useCashDrawerSlipPrinter } from '@/components/pos/PrintCashDrawerSlip'
import { useRotatingIdempotencyKey } from '@/components/pos/use-rotating-idempotency-key'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import { useCloseCashDrawerMutation } from '@/lib/services/cash-drawers/cash-drawers.query'
import { createCloseCashDrawerSchema } from '@/lib/services/cash-drawers/cash-drawers.schema'
import type { CashDrawerSession } from '@/lib/services/cash-drawers/cash-drawers.types'
import { formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import { LockKeyhole, Plus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export interface CashDrawerCloseWorkflowOptions {
  session: CashDrawerSession
  onSuccess?: () => unknown
}

export function useCashDrawerCloseWorkflow({ session, onSuccess }: CashDrawerCloseWorkflowOptions) {
  const mutation = useCloseCashDrawerMutation()
  const printSlip = useCashDrawerSlipPrinter()
  const [idempotencyKey, rotateIdempotencyKey] = useRotatingIdempotencyKey()
  const closeSchema = createCloseCashDrawerSchema(session.current)
  const form = useAppForm({
    defaultValues: {
      closing_counted_cash: '',
      closing_retained_cash: String(session.opening.counted_cash ?? ''),
      // Nothing to count when the till took no checks; otherwise staff must count them.
      closing_counted_checks: hasNoCheckActivity(session) ? '0.00' : '',
      note: '',
    },
    validators: {
      // The schema coerces the typed strings to numbers.
      onSubmit: submitSchemaValidator(closeSchema),
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          await mutation.mutateAsync({
            sessionId: session.id,
            closing_counted_cash: Number(value.closing_counted_cash),
            closing_retained_cash: Number(value.closing_retained_cash),
            closing_counted_checks: Number(value.closing_counted_checks),
            expected_totals_version: session.current.version,
            note: value.note.trim() || undefined,
            idempotency_key: idempotencyKey,
          })
        } catch (error) {
          toast.error('Unable to save the count', { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }

        rotateIdempotencyKey()
        toast.success('Cash drawer counted', {
          description: 'Print the slip to keep with the deposit.',
          duration: 15000,
          action: {
            label: 'Print slip',
            onClick: () => {
              void printSlip(session.id)
            },
          },
        })
        formApi.reset()
        await onSuccess?.()
        return undefined
      },
    },
  })

  const noCheckActivity = hasNoCheckActivity(session)
  useEffect(() => {
    // A payment invalidates the suggested zero, but never an operator's own count.
    if (
      !noCheckActivity &&
      !form.getFieldMeta('closing_counted_checks')?.isDirty &&
      form.getFieldValue('closing_counted_checks') === '0.00'
    ) {
      form.setFieldValue('closing_counted_checks', '', { dontUpdateMeta: true })
    }
  }, [form, noCheckActivity])

  return { form, mutation }
}

export type CashDrawerCloseWorkflowState = ReturnType<typeof useCashDrawerCloseWorkflow>

export type CashDrawerCloseWorkflowProps = CashDrawerCloseWorkflowOptions

export function CashDrawerCloseWorkflow({ session, onSuccess }: CashDrawerCloseWorkflowProps) {
  const workflow = useCashDrawerCloseWorkflow({ session, onSuccess })

  return <CashDrawerCloseWorkflowContent session={session} {...workflow} />
}

export interface CashDrawerCloseWorkflowContentProps extends CashDrawerCloseWorkflowState {
  session: CashDrawerSession
}

export function CashDrawerCloseWorkflowContent({
  form,
  mutation,
  session,
}: CashDrawerCloseWorkflowContentProps) {
  const [showNote, setShowNote] = useState(false)

  return (
    <form.AppForm>
      <form.Form>
        <Card size="flush" className="@container overflow-hidden">
          <CardContent className="space-y-6 px-5 py-6 @sm:px-6">
            <CashDrawerExpectedDisclosure
              lines={session.receipt.expected}
              expectedCash={session.current.expected_cash}
            />

            <form.Subscribe
              selector={(state) =>
                [
                  state.values.closing_counted_cash,
                  state.values.closing_counted_checks,
                  state.values.closing_retained_cash,
                ] as const
              }
            >
              {([cash, checks, retained]) => (
                <CloseCountLedger
                  form={form}
                  session={session}
                  cash={cash}
                  checks={checks}
                  retained={retained}
                />
              )}
            </form.Subscribe>

            <form.Subscribe
              selector={(state) =>
                [state.values.closing_counted_cash, state.values.closing_counted_checks] as const
              }
            >
              {([cash, checks]) => {
                const needsExplanation = hasTenderDifference(cash, checks, session)

                return showNote || needsExplanation ? (
                  <form.AppField name="note">
                    {(field) => (
                      <field.TextareaInput
                        label={
                          needsExplanation
                            ? 'Explanation for the differences (required)'
                            : 'Note about this count (optional)'
                        }
                        placeholder="Explain a difference or leave a note for the next person."
                        aria-required={needsExplanation}
                        rows={2}
                      />
                    )}
                  </form.AppField>
                ) : (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowNote(true)}>
                    <Plus className="size-4" />
                    Add a note about this count
                  </Button>
                )
              }}
            </form.Subscribe>
          </CardContent>

          <form.Subscribe
            selector={(state) =>
              [
                state.values.closing_counted_cash,
                state.values.closing_counted_checks,
                state.values.closing_retained_cash,
              ] as const
            }
          >
            {([cash, checks, retainedCash]) => {
              const hasRequiredAmounts = cash !== '' && checks !== '' && retainedCash !== ''

              return (
                <div className="flex flex-col gap-4 border-t bg-elevated p-5 @sm:flex-row @sm:items-center @sm:justify-between @sm:px-6">
                  <div className="min-w-0 space-y-1">
                    <Typography variant="labelStrong" className="block">
                      Finish the count
                    </Typography>
                    <Typography variant="supporting" className="block">
                      {hasRequiredAmounts
                        ? 'This locks the counts and finishes this drawer. Start it again before the next cash sale.'
                        : 'Count the cash and checks before finishing.'}
                    </Typography>
                  </div>
                  <form.SubmitButton
                    mutation={mutation}
                    disabled={!hasRequiredAmounts}
                    className="w-full shrink-0 @sm:w-auto"
                  >
                    <LockKeyhole className="size-4" />
                    Finish count
                  </form.SubmitButton>
                </div>
              )
            }}
          </form.Subscribe>
        </Card>
      </form.Form>
    </form.AppForm>
  )
}

// The live count follows accounting's balancing layout; the closed receipt and slip continue it.
interface CloseCountLedgerProps {
  form: CashDrawerCloseWorkflowState['form']
  session: CashDrawerSession
  cash: string
  checks: string
  retained: string
}

function CloseCountLedger({ form, session, cash, checks, retained }: CloseCountLedgerProps) {
  const hasCash = cash !== ''
  const hasChecks = checks !== ''
  const hasRetained = retained !== ''
  const cashDifferenceCents = hasCash
    ? calculateCashDifferenceCents(cash, session.current.expected_cash)
    : null
  const checkDifferenceCents = hasChecks
    ? calculateCashDifferenceCents(checks, session.current.expected_checks)
    : null
  const cashDifference =
    cashDifferenceCents === null ? null : formatTenderDifference(cashDifferenceCents)
  const checkDifference =
    checkDifferenceCents === null ? null : formatTenderDifference(checkDifferenceCents)
  const calculatedDepositCents = hasRetained
    ? calculateCashDepositCents(session.current.expected_cash, retained)
    : null
  const actualDepositCents =
    hasCash && hasChecks && hasRetained
      ? calculateCashDepositCents(cash, retained) + toCents(checks)
      : null
  const balanced =
    actualDepositCents !== null &&
    actualDepositCents >= 0 &&
    cashDifference?.tone === 'success' &&
    checkDifference?.tone === 'success'

  return (
    <div className="space-y-2">
      {/* The ledger updates silently as amounts are typed; this tells screen readers the outcome. */}
      <p role="status" className="sr-only">
        {describeCloseResult(cashDifferenceCents, checkDifferenceCents)}
      </p>
      <Card size="flush" surface="brandSubtle" rail="brand" className="overflow-hidden">
        <CardContent className="px-4 py-1">
          <Ledger aria-label="Actual count">
            <LedgerSection>
              <LedgerRow
                label="Actual counted cash and checks"
                emphasis="subtotal"
                hint={
                  <CashDenominationCounter
                    triggerVariant="link"
                    triggerSize="compact"
                    onChange={(amount: string) =>
                      form.setFieldValue('closing_counted_cash', amount)
                    }
                  />
                }
                valueWidth="auto"
                value={
                  <div className="flex items-end gap-2 @md/ledger:justify-end">
                    <div className="min-w-0 flex-1 @md/ledger:w-28 @md/ledger:flex-none">
                      <form.AppField name="closing_counted_cash">
                        {(field) => (
                          <field.MoneyInput
                            label="Cash"
                            aria-label="Cash counted"
                            align="right"
                            placeholder="0.00"
                            showFreeBadge={false}
                            formatEmptyAsZero={false}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <div className="min-w-0 flex-1 @md/ledger:w-28 @md/ledger:flex-none">
                      <form.AppField name="closing_counted_checks">
                        {(field) => (
                          <field.MoneyInput
                            label="Checks"
                            aria-label="Checks counted"
                            align="right"
                            placeholder="0.00"
                            showFreeBadge={false}
                            formatEmptyAsZero={false}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <div className="flex h-10 w-24 shrink-0 items-center justify-end">
                      <Typography variant="labelStrong" className="tabular-nums">
                        {hasCash && hasChecks
                          ? formatCurrency((toCents(cash) + toCents(checks)) / 100)
                          : '—'}
                      </Typography>
                    </div>
                  </div>
                }
              />
            </LedgerSection>
          </Ledger>
        </CardContent>
      </Card>
      <Ledger aria-label="Drawer count">
        <LedgerSection divided>
          <LedgerRow
            label="Cash left in the drawer"
            labelFor="closing_retained_cash"
            emphasis="subtotal"
            value={
              <form.AppField name="closing_retained_cash">
                {(field) => (
                  <field.MoneyInput align="right" showFreeBadge={false} formatEmptyAsZero={false} />
                )}
              </form.AppField>
            }
          />
          <LedgerRow
            label="Calculated cash for deposit"
            emphasis="subtotal"
            value={
              calculatedDepositCents === null ? '—' : formatCurrency(calculatedDepositCents / 100)
            }
          />
          <LedgerRow
            label="Actual counted cash and checks for deposit"
            emphasis="total"
            value={actualDepositCents === null ? '—' : formatCurrency(actualDepositCents / 100)}
            valueTone={
              actualDepositCents === null
                ? 'muted'
                : actualDepositCents < 0
                  ? 'destructive'
                  : 'default'
            }
            tone={balanced ? 'success' : undefined}
            badge={balanced ? <BalancedBadge /> : null}
          />
          <LedgerRow
            label="Cash (short)/over"
            emphasis="subtotal"
            hint={cashDifference?.hint}
            value={cashDifference?.text ?? '—'}
            valueTone={cashDifference?.tone ?? 'muted'}
            tone={cashDifference?.band}
          />
          <LedgerRow
            label="Checks (short)/over"
            emphasis="subtotal"
            hint={checkDifference?.hint}
            value={checkDifference?.text ?? '—'}
            valueTone={checkDifference?.tone ?? 'muted'}
            tone={checkDifference?.band}
          />
        </LedgerSection>
      </Ledger>
    </div>
  )
}

function describeCloseResult(cashCents: number | null, checkCents: number | null) {
  if (cashCents === null || checkCents === null) return ''

  const tenders: Array<[string, number]> = [
    ['Cash', cashCents],
    ['Checks', checkCents],
  ]
  const differences = tenders
    .filter(([, cents]) => cents !== 0)
    .map(
      ([tender, cents]) =>
        `${tender} ${cents < 0 ? 'short' : 'over'} ${formatCurrency(Math.abs(cents) / 100)}.`
    )

  return differences.length
    ? `${differences.join(' ')} Explain the difference before finishing.`
    : 'Balanced. Cash and checks match what the register expected.'
}

function toCents(value: string) {
  return Math.round(Number(value || 0) * 100)
}

function hasTenderDifference(cash: string, checks: string, session: CashDrawerSession) {
  return (
    (cash !== '' && calculateCashDifferenceCents(cash, session.current.expected_cash) !== 0) ||
    (checks !== '' && calculateCashDifferenceCents(checks, session.current.expected_checks) !== 0)
  )
}

function hasNoCheckActivity(session: CashDrawerSession) {
  return (
    Number(session.current.check_count || 0) === 0 &&
    Math.round(parseCurrency(session.current.expected_checks) * 100) === 0
  )
}
